import { saveToLocalStorage, fetchFromLocalStorage, clearLocalStorage } from '../api/localStorage';
import { VALID_TIERS, VALID_YEARLY, type ValidTier } from '../constants/subscriptionTiers';

/**
 * Validates and saves tier and yearly URL parameters to localStorage
 * @returns {Object} Object containing the saved values and validation status
 */
export const validateAndSaveUrlParams = (): {
  tier?: ValidTier;
  yearly?: boolean;
  hasValidParams: boolean;
  updatedValues: {
    tier?: ValidTier;
    yearly?: boolean;
  };
} => {
  const urlParams = new URLSearchParams(window.location.search);
  const tierParam = urlParams.get('tier');
  const yearlyParam = urlParams.get('yearly');
  const tenantIdParam = urlParams.get('tenant_id');
  const inviteHashParam = urlParams.get('invite_hash');
  
  let hasValidParams = false;
  let tier: ValidTier | undefined;
  let yearly: boolean | undefined;

  // Validate tier parameter
  if (tierParam && VALID_TIERS.includes(tierParam as ValidTier)) {
    tier = tierParam as ValidTier;
    saveToLocalStorage('tier', tier);
    hasValidParams = true;
  }

  // Validate yearly parameter
  if (yearlyParam === VALID_YEARLY) {
    yearly = true;
    saveToLocalStorage('yearly', true);
    hasValidParams = true;
  } else if (yearlyParam === 'false') {
    yearly = false;
    saveToLocalStorage('yearly', false);
    hasValidParams = true;
  } else if (yearlyParam === null && (tierParam || tenantIdParam || inviteHashParam)) {
    // If yearly parameter is missing but other params exist, clear the yearly value
    clearLocalStorage('yearly');
    yearly = undefined;
  }

  // Save invitation parameters if present (no validation here; backend validates)
  if (inviteHashParam) {
    if (tenantIdParam) {
      saveToLocalStorage('tenant_id', tenantIdParam);
    }
    saveToLocalStorage('invite_hash', inviteHashParam);
    hasValidParams = true;
  }

  // Get all current saved values (including newly updated ones)
  const currentSavedValues = getSavedUrlParams();

  return {
    tier,
    yearly,
    hasValidParams,
    updatedValues: currentSavedValues
  };
};

/**
 * Retrieves saved tier and yearly values from localStorage
 * @returns {Object} Object containing the saved values
 */
export const getSavedUrlParams = (): {
  tier?: ValidTier;
  yearly?: boolean;
  tenant_id?: string;
  invite_hash?: string;
} => {
  const tier = fetchFromLocalStorage('tier') as ValidTier | null;
  const yearly = fetchFromLocalStorage('yearly') as boolean | null;
  const tenant_id = fetchFromLocalStorage('tenant_id') as string | null;
  const invite_hash = fetchFromLocalStorage('invite_hash') as string | null;

  return {
    tier: tier && VALID_TIERS.includes(tier) ? tier : undefined,
    yearly: yearly || undefined,
    tenant_id: tenant_id || undefined,
    invite_hash: invite_hash || undefined
  };
};

/**
 * Clears saved tier and yearly values from localStorage and URL parameters
 */
export const clearSavedUrlParams = (): void => {
  // Clear localStorage values
  clearLocalStorage('tier');
  clearLocalStorage('yearly');
  clearLocalStorage('tenant_id');
  clearLocalStorage('invite_hash');
  
  // Clear URL parameters from the browser's address bar
  const url = new URL(window.location.href);
  url.searchParams.delete('tier');
  url.searchParams.delete('yearly');
  url.searchParams.delete('tenant_id');
  url.searchParams.delete('invite_hash');
  
  // Update the URL without triggering a page reload
  window.history.replaceState({}, '', url.toString());
}; 

/**
 * Returns only invitation-related saved params
 */
export const getSavedInviteParams = (): {
  tenant_id?: string;
  invite_hash?: string;
} => {
  const tenant_id = fetchFromLocalStorage('tenant_id') as string | null;
  const invite_hash = fetchFromLocalStorage('invite_hash') as string | null;
  return {
    tenant_id: tenant_id || undefined,
    invite_hash: invite_hash || undefined,
  };
};

/**
 * Clears only invitation-related saved params and URL
 */
export const clearSavedInviteParams = (): void => {
  clearLocalStorage('tenant_id');
  clearLocalStorage('invite_hash');

  const url = new URL(window.location.href);
  url.searchParams.delete('tenant_id');
  url.searchParams.delete('invite_hash');
  window.history.replaceState({}, '', url.toString());
};